import {
  Document,
  Font,
  Image,
  Page,
  pdf,
  PDFViewer,
  StyleSheet,
  Text,
  View
} from '@react-pdf/renderer'

import { useSSRWishlist } from '@/hooks'
import { selectEnabledWishlist } from '@/store/wishlist'
import { getColorCategoryLabel } from '@/utils/color'

import { Color } from '@/types'

type WishlistPdfProps = {
  wishlist: Color[]
}

const ROWS_PER_PAGE = 7

Font.register({
  family: 'HelveticaNow',
  fonts: [
    { src: `/pdf/fonts/HelveticaNeueLTStd-Roman.otf`, fontStyle: 'normal', fontWeight: 400 },
    { src: `/pdf/fonts/HelveticaNeueLTStd-Bd.otf`, fontStyle: 'normal', fontWeight: 700 }
  ]
})

const styles = StyleSheet.create({
  page: {
    fontFamily: 'HelveticaNow',
    backgroundColor: '#ffffff',
    color: '#202020',
    display: 'flex',
    flexDirection: 'column',
    padding: 32,
    gap: 48
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    flexShrink: 0,
    flexGrow: 0
  },
  logo: {
    height: 24,
    width: 123
  },
  meta: {
    fontSize: 11,
    opacity: 0.7,
    textTransform: 'uppercase'
  },
  view: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    flexShrink: 0,
    flexGrow: 1
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 0.5,
    borderBottomStyle: 'solid',
    borderBottomColor: '#d2d2d2'
  },
  color: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
    flexGrow: 1
  },
  colorSwatch: {
    width: 48,
    height: 48,
    borderRadius: 3,
    flexShrink: 0
  },
  colorName: {
    fontSize: 9,
    fontWeight: 700,
    marginLeft: 8
  },
  colorHex: {
    fontSize: 8,
    marginLeft: 8,
    opacity: 0.5
  },
  finishing: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexBasis: '30%',
    flexShrink: 0,
    flexGrow: 0,
    marginLeft: 16
  },
  finishingSwatch: {
    width: 48,
    height: 48,
    borderRadius: 3,
    overflow: 'hidden',
    border: '1px dashed #d2d2d2'
  },
  finishingImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  finishingName: {
    fontSize: 9,
    fontWeight: 700,
    marginLeft: 8
  },
  details: {
    height: 48,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexBasis: '25%',
    marginLeft: 16,
    flexShrink: 0,
    flexGrow: 0
  },
  detailsText: {
    fontSize: 9,
    fontWeight: 700,
    textTransform: 'uppercase',
    textAlign: 'right',
    flexShrink: 0,
    flexGrow: 1
  }
})

const DocumentHeader = () => (
  <View style={styles.header}>
    {/* eslint-disable-next-line jsx-a11y/alt-text */}
    <Image src="/pdf/assets/logo.svg" style={styles.logo} />
    <Text style={styles.meta}>Wishlist</Text>
  </View>
)

const WishlistDocument = ({ wishlist }: WishlistPdfProps) => {
  const pages = Array.from({ length: Math.ceil(wishlist.length / ROWS_PER_PAGE) }, (_, pageIndex) =>
    wishlist.slice(pageIndex * ROWS_PER_PAGE, (pageIndex + 1) * ROWS_PER_PAGE)
  )

  return (
    <Document title="Wishlist Coloridecora">
      {pages.map((pageWishlist, pageIndex) => (
        <Page key={pageIndex} size="A4" orientation="landscape" style={styles.page}>
          <DocumentHeader />

          <View style={styles.view}>
            {pageWishlist.map((color) => (
              <View key={color.id} style={styles.row}>
                <View style={styles.color}>
                  <View style={[styles.colorSwatch, { backgroundColor: color.hex_color }]} />
                  <Text style={styles.colorName}>{color.title}</Text>
                  <Text style={styles.colorHex}>{`[${color.hex_color}]`}</Text>
                </View>

                {color.finishing ? (
                  <View style={styles.finishing}>
                    <View style={styles.finishingSwatch}>
                      {/* eslint-disable-next-line jsx-a11y/alt-text */}
                      <Image
                        src={color.finishing.acf.image_thumb.url}
                        style={styles.finishingImage}
                      />
                    </View>
                    <Text style={styles.finishingName}>{color.finishing.name}</Text>
                  </View>
                ) : null}

                <View style={styles.details}>
                  <Text style={styles.detailsText}>{getColorCategoryLabel(color)}</Text>
                </View>
              </View>
            ))}
          </View>
        </Page>
      ))}
    </Document>
  )
}

const WishlistPdfRenderer = () => {
  const ssrWishlist = useSSRWishlist()
  const wishlist = selectEnabledWishlist(ssrWishlist)

  return (
    <PDFViewer className="size-full">
      <WishlistDocument wishlist={wishlist} />
    </PDFViewer>
  )
}

const createWishlistPdf = (wishlist: Color[]) =>
  pdf(<WishlistDocument wishlist={wishlist} />).toBlob()

export { createWishlistPdf, WishlistDocument, WishlistPdfRenderer }
